"use client"

/**
 * Pieces every products-home variant is built from.
 *
 * The variants disagree about layout and agree about vocabulary: they all
 * draw a product's mark the same way, state scope the same way, and say the
 * same thing when the workspace has no products. Those live here so a fourth
 * variant is a layout decision rather than a copy-and-paste of the third.
 *
 * *not* live here, still: mosaic row shapes — Spotlight keeps those in
 * `home-mosaic.tsx`, and Campus borrows only its stretched-link and arrow
 * classes. `OwnedProductTile` is shared by Storefront, Focus, and Launcher so
 * Your App is one card everywhere.
 */

import * as React from "react"
import { useReducedMotion } from "motion/react"
import { Link, useNavigate } from "react-router"

import { useLeoAmbience } from "@/components/leo-ambience-context"
import { LeoPulseBorder } from "@/components/leo-pulse-border"
import { LeoThinkingBackdrop } from "@/components/leo-thinking-backdrop"
import { badgeVariants } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  HorizontalScrollControls,
  HorizontalScrollViewport,
  useHorizontalScrollAffordances,
} from "@/components/ui/horizontal-scroll-region"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel"
import { MarketingBanner } from "@/components/ui/marketing-banner"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useScopeSwitcher } from "@/components/scope-switcher-menu"
import { usePersistedState } from "@exxatdesignux/ui/lib/persisted-state"
import type { Product } from "@/contexts/product-context"
import { directoryLaunchSummaries } from "@/lib/product-home"
import {
  openableIdentities,
  openAs,
  OPEN_AS_LABEL,
  type WorkspaceRole,
} from "@/lib/login-session"
import {
  LEO_WINDOW_COVER_IMAGE,
  LEO_WINDOW_PREVIEW_IMAGE,
  type ProductWhatsNewItem,
} from "@/lib/mock/product-catalog"
import {
  collectWhatsNewUpdates,
  whatsNewUpdateId,
  type ProductHomeCard,
} from "@/lib/product-home"
import { isSchoolScopedProduct } from "@/lib/scope-switcher"
import { STUDENT_HOME_PATH } from "@/lib/student-shell"
import { cn } from "@/lib/utils"
import { canReadDirectory } from "@/lib/workspace-role"

import { AdminAppPicker, useAdminAppScope } from "./admin-app-picker"
import { ProductMarkTile } from "./product-mark-tile"
import { ProductScopePicker } from "./product-scope-picker"
import { shelfCardField } from "./product-shelves"
import { SectionHeading } from "./section-heading"
import { WhatsNewLeoAmbience, WhatsNewLeoMark } from "./whats-new-leo-ambience"

/** Shared by every variant body, so the page can hand all three the same bag. */
export interface HomeBodyProps {
  owned: ProductHomeCard[]
  available: ProductHomeCard[]
  workspaceName: string
  onOpen: (card: ProductHomeCard) => void
  /**
   * Whether the sign-in that opened this session asked for these sections. A
   * flag rather than an empty list, because empty means something else on this
   * page: no owned products draws "no apps yet", and that would be a lie told
   * about a workspace that has them.
   */
  showYourApp: boolean
  showMoreFromExxat: boolean
}

/** Shared vertical stack for every `/home/*` variant body. */
export function HomeVariantBody({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("flex w-full flex-col gap-10", className)}>
      {children}
      {/* Explicit tail — padding on flex scrollports was not reachable at max scroll. */}
      <div aria-hidden className="h-32 w-full shrink-0" />
    </div>
  )
}

/**
 * Dismissal is per device and not product-scoped, so the key stays shell-global.
 * Shared across variants: dismissing the promotion is a statement about the
 * promotion, not about the layout you happened to be in when you made it.
 */
export const FEATURED_DISMISSED_KEY = "home-featured-dismissed"

/**
 * Which unowned product gets the promoted slot, in whichever variant has one.
 *
 * Named rather than derived. The rule used to be "whatever is newest", which
 * sounds objective and is not: a `New` chip means the product shipped recently,
 * while the promoted slot is a decision about what this workspace should look at
 * next, and those come apart the moment two products carry a stage. Marketing
 * owns this line; callers fall back to the first available product so the slot
 * cannot render empty if the named product ever becomes part of the plan.
 */
export const FEATURED_PRODUCT: Product = "exxat-exam-management"

export function firstName(full: string): string {
  return full.split(/\s+/)[0] ?? full
}

/**
 * Time-of-day greeting for the variants that lead with one.
 *
 * Shared rather than duplicated per variant, so "Good morning" doesn't drift
 * into "Welcome back" on one page and stay put on another.
 */
export function greetingForHour(hour: number): string {
  if (hour < 5) return "Still up"
  if (hour < 12) return "Good morning"
  if (hour < 17) return "Good afternoon"
  return "Good evening"
}

/**
 * Greeting cover banner — solid More from Exxat tint, greeting copy anchored
 * toward the foot. Leo blobs use the cover wash and play once on load.
 */
export function HomeGreetingShelfCard({
  brandColor,
  children,
  className,
}: {
  brandColor: string
  children: React.ReactNode
  className?: string
}) {
  const reduceMotion = useReducedMotion() ?? false
  const { prefs } = useLeoAmbience()
  const usePulse = prefs.thinkingAnimationStyle === "pulse"
  const washOn = prefs.greetingBannerWash && prefs.thinkingBlob
  const showBlobs = !reduceMotion && washOn && !usePulse
  const showPulse = !reduceMotion && washOn && usePulse
  const washOpacity = prefs.thinkingBlobOpacity

  return (
    <article
      className={cn(
        "relative flex h-40 w-full min-w-0 flex-col overflow-hidden rounded-xl border border-border font-sans shadow-xs",
        className,
      )}
      style={{ backgroundColor: shelfCardField(brandColor) }}
    >
      {showBlobs ? (
        <LeoThinkingBackdrop
          thinking={false}
          intensity="high"
          opacity={washOpacity * prefs.greetingBannerWashStrength}
          animate={prefs.blobAnimations}
          playOnce
          animationSpeed={prefs.blobAnimationSpeed}
          className="rounded-xl"
        />
      ) : null}
      {showPulse ? (
        <LeoPulseBorder
          active
          strength={Math.min(
            1,
            washOpacity * prefs.greetingBannerWashStrength,
          )}
          animate={prefs.blobAnimations}
          animationSpeed={prefs.blobAnimationSpeed}
          className="rounded-xl"
        />
      ) : null}

      <div className="relative z-[1] flex min-h-0 flex-1 flex-col justify-end p-4 sm:p-5">
        {children}
      </div>
    </article>
  )
}

/**
 * Re-exported rather than moved out of sight: this file is the vocabulary every
 * variant imports from, and the mark tile is part of that vocabulary. It only
 * lives elsewhere to keep the picker below from importing this file back.
 */
export { ProductMarkTile }

/** The scope pill, or the line that stands in for it on unscoped products. */
export function ProductScopeLine({
  card,
  variant,
  className,
}: {
  card: ProductHomeCard
  variant: "inline" | "card" | "compact"
  className?: string
}) {
  const scope = useScopeSwitcher(card.product, card.customIndex)

  if (!card.scoped) {
    return (
      <p
        className={cn(
          "flex min-w-0 items-center text-muted-foreground",
          variant === "card" ? "px-1 py-2 text-sm" : "px-1.5 py-1 text-xs",
          className,
        )}
      >
        <i className="fa-light fa-globe me-1.5 text-[0.85em]" aria-hidden="true" />
        Whole workspace
      </p>
    )
  }

  return (
    <ProductScopePicker
      scope={scope}
      variant={variant}
      className={cn(variant !== "card" && "text-xs", className)}
    />
  )
}

/** Dismissal is keyed per update, not per product, so a later release re-earns the badge. */
const WHATS_NEW_SEEN_KEY = "home-whats-new-seen"

/**
 * One `seen` list, shared by every `WhatsNewBadge` and the `WhatsNewSection`
 * digest on a page instead of each calling `usePersistedState` on its own.
 *
 * `usePersistedState` only syncs *across tabs* (the browser's own `storage`
 * event never fires in the tab that made the write) — two independent calls
 * to it with the same key inside *one* document drift the moment either one
 * writes, same as the reason `FEATURED_DISMISSED_KEY` is owned in one place
 * per variant. A product card's badge and the digest row for that same
 * update are the same fact rendered twice, so dismissing one has to be seen
 * by the other on the same click, not after a reload — hence a context that
 * owns the list once per page instead of prop-drilling `seen`/`dismiss`
 * through every card component between the two.
 */
const WhatsNewContext = React.createContext<{
  seen: string[]
  dismiss: (id: string) => void
} | null>(null)

/**
 * Mounted once in `App`, above the shell.
 *
 * It used to wrap only the `/home` body, which was enough while the page was the
 * only place this list was read. The utility bar's What's new button reads it too
 * and mounts in a different subtree, so the provider has to sit above both or the
 * two subtrees each keep their own copy.
 */
export function WhatsNewProvider({ children }: { children: React.ReactNode }) {
  const [seen, setSeen] = usePersistedState<string[]>(WHATS_NEW_SEEN_KEY, [])
  const dismiss = React.useCallback(
    (id: string) => setSeen(prev => (prev.includes(id) ? prev : [...prev, id])),
    [setSeen],
  )
  const value = React.useMemo(() => ({ seen, dismiss }), [seen, dismiss])
  return <WhatsNewContext.Provider value={value}>{children}</WhatsNewContext.Provider>
}

/**
 * Falls back to an unshared instance outside a provider, rather than throwing.
 *
 * The fallback is for isolated renders (catalog previews, tests) that mount a
 * badge without the shell around it. In the app the provider is always above,
 * which is what lets an update dismissed in the utility bar stop nagging from
 * the product tiles and the digest in the same paint.
 */
export function useWhatsNewSeen() {
  const context = React.useContext(WhatsNewContext)
  const [localSeen, setLocalSeen] = usePersistedState<string[]>(
    context ? "" : WHATS_NEW_SEEN_KEY,
    [],
  )
  if (context) return context
  return {
    seen: localSeen,
    dismiss: (id: string) => setLocalSeen(prev => (prev.includes(id) ? prev : [...prev, id])),
  }
}

/**
 * "New" flag for an owned product with a recent change worth calling out.
 *
 * A `Popover`, not a `Tooltip` — the update is a sentence of real content
 * (`card.whatsNew[0].body`) plus a dismiss action, and a tooltip vanishes the
 * moment a mouse or keyboard user reaches for either. The trigger is a plain
 * button styled with `badgeVariants` rather than `Badge` itself: `Badge`
 * renders a `span` (or, via `asChild`, wants to *be* the one interactive
 * element), and this one has to be a real, focusable, `aria-label`-carrying
 * control that Radix can also anchor the popover to.
 *
 * Dismissal is keyed to *this* update's own text, not just the product's
 * slug — so if `whatsNew` ever changes to describe a later release, the
 * badge reappears instead of staying silently dismissed for a change nobody
 * has actually seen.
 */
export function WhatsNewBadge({
  card,
  className,
}: {
  card: ProductHomeCard
  className?: string
}) {
  const { seen, dismiss: markSeen } = useWhatsNewSeen()
  const [open, setOpen] = React.useState(false)
  // Only the newest entry — see the "newest first" note on
  // `ProductCatalogEntry.whatsNew`. Older, still-unseen entries stay
  // reachable in the `/home` digest (`WhatsNewSection`) without also
  // queuing up behind this tile's one badge.
  const whatsNew = card.whatsNew?.[0]
  const updateId = whatsNew ? whatsNewUpdateId(card.slug, whatsNew.title) : null

  if (!whatsNew || !updateId || seen.includes(updateId)) return null

  function dismiss() {
    markSeen(updateId!)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`New in ${card.label}: ${whatsNew.title}. Show details.`}
          className={cn(
            badgeVariants({ variant: "default" }),
            "px-2 py-0.5 text-2xs",
            className,
          )}
        >
          New
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-4">
        <div className="flex flex-col gap-2">
          <p className="text-2xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
            New in {card.label}
          </p>
          <p className="text-sm font-semibold leading-snug">{whatsNew.title}</p>
          <p className="text-sm leading-relaxed text-muted-foreground">{whatsNew.body}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-1 self-start"
            onClick={dismiss}
          >
            Got it
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * The recent-changes digest — one `MarketingBanner` per unseen update across
 * every owned product, in one place instead of buried in whichever card
 * happens to carry a `WhatsNewBadge`.
 *
 * `MarketingBanner layout="hero"` — the same primitive `PromoStrip`
 * (`product-marketing-page.tsx`) already uses for "here is one thing, act on
 * it" copy, rather than a bespoke card. A dedicated card would have to
 * reinvent the eyebrow/title/body/action rhythm the hero layout already
 * gets right, and would drift from it the first time either one changed
 * without the other (`exxat-reuse-before-custom`).
 *
 * `tone="surface"` plus an inline border tint. Most cards keep a soft
 * product-colour wash (`whatsNewWash`) and a component screenshot in the hero
 * column. Only **New Ask Leo** drops the wash, wraps in `WhatsNewLeoAmbience`
 * (one brand thinking wash, no cursor field), and shows the interactive Leo
 * mark in that same column.
 *
 * Shares `WHATS_NEW_SEEN_KEY` with `WhatsNewBadge`: dismissing an update here
 * (the banner's own built-in dismiss control) also clears its badge on the
 * product card, and the reverse, because they are the same fact shown twice,
 * not two separate things to dismiss.
 *
 * Renders nothing once every update is seen — an empty "What's new" heading
 * sitting above the product grid every single day is worse than the section
 * simply not existing on the days there is nothing to say.
 *
 * A horizontal scroll strip, not a stacked list — this digest is *across
 * every owned product*, so a workspace with a full catalog can have a dozen
 * unseen updates on one day. Stacking full-width hero banners would push the
 * product grid below the fold; a fixed-width row scrolls instead. Prev/next
 * chevrons live in the section header for the scroll strip, or on the banner
 * actions row for the one-at-a-time carousel in narrow rails.
 *
 * Each banner carries both actions the update needs directly — `Try it`
 * opens the product itself, `Register for webinar` flips to a disabled
 * "You're registered" once clicked (`MarketingBannerAction`'s own
 * `disabled` state, built for exactly this "can only be taken once" case) —
 * so there is no second click-through to a detail sheet repeating the same
 * two buttons.
 *
 * Two `mediaFit`s, picked per update by `ProductWhatsNewItem.visual`.
 * `"contain"` (default, `visual: "component"`) frames the shot like the
 * floating window or KPI card it is a screenshot *of* — capped height,
 * rounded border, its own shadow — sitting beside the copy at a size that
 * reads as one component, not stretched to stand in for the whole product.
 * `"cover"` (`visual: "cover"`) drops the two-column split and lets the
 * screenshot fill the whole card behind a scrim, for an update that *is* a
 * whole screen rather than one component on it. `compactDescription` clamps
 * the copy to two lines for the same reason a table row does not grow to
 * fit its longest cell — one long update should not make its card taller
 * than its neighbours in the same scrolling row.
 *
 * `ProductWhatsNewItem.image` overrides the product's own screenshot
 * (`entry.screens[0]`, the fallback below). **New Ask Leo** skips the still
 * (it is the same star the live mark already draws) and uses the mark in the
 * hero column.
 */
function whatsNewWash(color: string): string {
  return `linear-gradient(135deg,
    color-mix(in oklch, ${color} 14%, transparent) 0%,
    color-mix(in oklch, ${color} 5%, transparent) 55%,
    transparent 100%)`
}

/** Only the Ask Leo launch card — not every Leo-titled update. */
function isNewAskLeoWhatsNew(item: ProductWhatsNewItem): boolean {
  return (
    item.image === LEO_WINDOW_COVER_IMAGE ||
    /^new\s+ask\s+leo\b/i.test(item.title.trim())
  )
}

export function WhatsNewSection({
  owned,
  onOpen,
  className,
  layout = "scroll",
}: {
  owned: ProductHomeCard[]
  onOpen: (card: ProductHomeCard) => void
  className?: string
  /** Carousel shows one card at a time — use in narrow rails and on mobile. */
  layout?: "scroll" | "carousel"
}) {
  const { seen, dismiss } = useWhatsNewSeen()
  const [webinarRequested, setWebinarRequested] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const { canScrollLeft, canScrollRight, overflowing, scrollPrev, scrollNext } =
    useHorizontalScrollAffordances(scrollRef)
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>()
  const [canCarouselPrev, setCanCarouselPrev] = React.useState(false)
  const [canCarouselNext, setCanCarouselNext] = React.useState(false)

  const seenIds = React.useMemo(() => new Set(seen), [seen])
  const updates = collectWhatsNewUpdates(owned)
    .filter(update => !seenIds.has(update.id))
    .map(({ id, card, item }) => ({ id, card, whatsNew: item }))

  React.useEffect(() => {
    if (layout !== "carousel" || !carouselApi) return

    const onSelect = () => {
      setCanCarouselPrev(carouselApi.canScrollPrev())
      setCanCarouselNext(carouselApi.canScrollNext())
    }

    onSelect()
    carouselApi.on("select", onSelect)
    carouselApi.on("reInit", onSelect)

    return () => {
      carouselApi.off("select", onSelect)
      carouselApi.off("reInit", onSelect)
    }
  }, [carouselApi, layout])

  if (updates.length === 0) return null

  const showCarouselControls =
    layout === "carousel" &&
    updates.length > 1 &&
    (canCarouselPrev || canCarouselNext)

  const showScrollControls = layout !== "carousel" && overflowing

  const carouselNav = showCarouselControls ? (
    <HorizontalScrollControls
      ariaLabel="What's new"
      canScrollLeft={canCarouselPrev}
      canScrollRight={canCarouselNext}
      onScrollPrev={() => carouselApi?.scrollPrev()}
      onScrollNext={() => carouselApi?.scrollNext()}
    />
  ) : null

  const renderUpdate = ({
    id,
    card,
    whatsNew,
  }: {
    id: string
    card: ProductHomeCard
    whatsNew: ProductWhatsNewItem
  }) => {
    const askLeo = isNewAskLeoWhatsNew(whatsNew)
    const imageSrc = askLeo
      ? undefined
      : (whatsNew.image === LEO_WINDOW_COVER_IMAGE
          ? LEO_WINDOW_PREVIEW_IMAGE
          : (whatsNew.image ?? card.entry?.screens[0]?.src))
    const borderColor = `color-mix(in oklch, ${card.brandColor} 24%, transparent)`
    // Leo mark stays in the hero column (`contain`). `cover` would paint it as a
    // full-bleed background behind the copy. Component screenshots use the same
    // side column so the art sits on the right, not under the title.
    const mediaFit: "cover" | "contain" =
      askLeo || whatsNew.visual !== "cover" ? "contain" : "cover"
    const banner = (
      <MarketingBanner
        layout="hero"
        tone="surface"
        title={whatsNew.title}
        eyebrow={`New in ${card.label}`}
        eyebrowIcon={card.icon}
        media={
          askLeo ? (
            <WhatsNewLeoMark />
          ) : imageSrc ? (
            <img
              src={imageSrc}
              alt=""
              className={
                mediaFit === "cover"
                  ? "size-full object-cover object-center"
                  : "max-h-28 w-auto rounded-lg border border-border object-contain shadow-md lg:max-h-32"
              }
            />
          ) : undefined
        }
        mediaFit={mediaFit}
        compact
        compactDescription
        primaryAction={{ label: "Try it", onClick: () => onOpen(card) }}
        secondaryAction={{
          label: webinarRequested.has(id) ? "You\u2019re registered" : "Register for webinar",
          disabled: webinarRequested.has(id),
          onClick: () => setWebinarRequested(prev => new Set(prev).add(id)),
        }}
        actionsTrailing={layout === "carousel" ? carouselNav : undefined}
        dismissible
        onDismiss={() => dismiss(id)}
        className={
          layout === "carousel"
            ? askLeo
              ? "border-0 bg-transparent shadow-none"
              : undefined
            : askLeo
              ? "h-full overflow-hidden border-0 bg-transparent shadow-none"
              : "h-full overflow-hidden"
        }
        style={
          askLeo
            ? undefined
            : { backgroundImage: whatsNewWash(card.brandColor), borderColor }
        }
      >
        {whatsNew.body}
      </MarketingBanner>
    )

    return askLeo ? (
      <WhatsNewLeoAmbience
        className={layout === "carousel" ? undefined : "h-full"}
        style={{ borderColor }}
      >
        {banner}
      </WhatsNewLeoAmbience>
    ) : (
      banner
    )
  }

  return (
    <section
      aria-labelledby="whats-new-heading"
      className={cn(
        "flex min-w-0 flex-col",
        layout === "carousel" ? "gap-2" : "gap-3",
        className,
      )}
    >
      <SectionHeading
        id="whats-new-heading"
        title={"What\u2019s new"}
        actions={
          showScrollControls ? (
            <HorizontalScrollControls
              ariaLabel="What's new"
              canScrollLeft={canScrollLeft}
              canScrollRight={canScrollRight}
              onScrollPrev={scrollPrev}
              onScrollNext={scrollNext}
            />
          ) : null
        }
      />
      {layout === "carousel" ? (
        <Carousel
          setApi={setCarouselApi}
          opts={{ align: "start" }}
          className="min-w-0 w-full"
        >
          <CarouselContent className="items-start">
            {updates.map(update => (
              <CarouselItem key={update.id} className="basis-full">
                {renderUpdate(update)}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      ) : (
        <HorizontalScrollViewport
          ref={scrollRef}
          label="What's new"
          className="items-stretch pb-1 pt-0.5"
        >
          <ul className="flex shrink-0 list-none gap-4 p-0">
            {updates.map(update => (
              <li key={update.id} className="w-[30rem] shrink-0 list-none sm:w-[34rem]">
                {renderUpdate(update)}
              </li>
            ))}
          </ul>
        </HorizontalScrollViewport>
      )}
    </section>
  )
}

/**
 * Directory — People Management and Course Management as one row of chips.
 *
 * Shared by every `/home` variant that renders Directory in the reading
 * order (Storefront, Focus, Launcher) instead of each drawing its own
 * version — the one row here reused counts among the fixes this pass makes,
 * not a fourth style to keep in sync by hand. Spotlight keeps a Directory
 * heading over `MosaicWaitingRow`. Campus puts those same hubs under Your App
 * as a hairline strip, still behind `canReadDirectory`.
 * Personnel Management is site-scoped and is not a launch chip.
 *
 * `variant="outline"` — a visible border reads as "this is a control" before
 * the pointer arrives, unlike a translucent fill alone, which only reads as
 * clickable once the cursor is already on it. No count beside the label:
 * Directory here is a set of destinations to open, not a dashboard of
 * totals, and a coordinator who wants "how many" already has that on the
 * People/Courses hub itself.
 *
 * Everyone but a student, on `canReadDirectory` rather than the console's own
 * predicate: these are the rosters a coordinator works from all day, so gating
 * them on administering the workspace left faculty unable to look up the people
 * they place. A student is the floor, because a student who could open them would
 * be reading their whole cohort. The gate sits inside the component so all three
 * variants that render it inherit it, rather than three call sites each
 * remembering to ask.
 */
export function HomeDirectorySection() {
  const objects = React.useMemo(() => directoryLaunchSummaries(), [])
  if (!canReadDirectory()) return null

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading title="Directory" />
      <ul className="flex list-none flex-wrap items-center gap-2 p-0">
        {objects.map(object => (
          <li key={object.id}>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-1.5 rounded-full bg-muted/25 font-medium hover:bg-muted/50"
            >
              <Link to={object.href}>
                <i className={cn("fa-solid text-sm", object.icon)} aria-hidden="true" />
                {object.label}
              </Link>
            </Button>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * The identities this card should offer, or none for the ordinary single door.
 *
 * Two conditions, and both are about honesty rather than taste. The session has
 * to hold more than one identity, which only a sign-in can grant. And the product
 * has to be scoped to a school and program, because "as a student" means a
 * student *of a program* — a site product has no student view to open, so its
 * card keeps one Open.
 */
export function openableDoors(card: ProductHomeCard): WorkspaceRole[] {
  if (card.href || !isSchoolScopedProduct(card.product)) return []
  return openableIdentities()
}

/**
 * One door, or one per identity when the person holds two.
 *
 * Every other card on this page has exactly one action, and this is the one place
 * that is wrong: someone who is both a student in the program and staff in it is
 * looking at a fork, not a door. Hiding the second branch behind a menu on the
 * first would mean the identity you got was the default nobody chose, which is
 * the bug this replaces.
 *
 * Both buttons stay `outline`, at equal weight. Neither identity is the
 * recommended one — only the person reaching for the card knows which they are
 * today — and promoting one to `default` would answer the question the card is
 * asking. They wrap rather than shrink, so the labels never truncate to `Open
 * as…` on a narrow card.
 *
 * Each accessible name carries the product, because a two-column grid of these
 * would otherwise read out as several identical pairs of "Open as student" with
 * nothing to tell them apart. The visible words are a prefix of the spoken ones,
 * so voice control still matches what is on screen.
 */
function OpenDoors({
  card,
  doors,
  onOpen,
}: {
  card: ProductHomeCard
  doors: WorkspaceRole[]
  onOpen: (card: ProductHomeCard, role: WorkspaceRole) => void
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {doors.map(role => (
        <Button
          key={role}
          type="button"
          variant="outline"
          className="min-w-[9rem] flex-1"
          aria-label={`Open as ${OPEN_AS_LABEL[role]} in ${card.label}`}
          onClick={() => onOpen(card, role)}
        >
          Open as {OPEN_AS_LABEL[role]}
          <i className="fa-light fa-arrow-right text-sm" aria-hidden="true" />
        </Button>
      ))}
    </div>
  )
}

/**
 * Take one of the doors: become that identity, then go where it leads.
 *
 * The write happens before the navigation, not after, because the page being
 * opened reads the role while it renders — the chrome asks whether scope is a
 * picker, and the shell asks whether the console answers at all.
 *
 * A student goes to the student home rather than into the product's dashboard.
 * That is the whole difference between the two doors: staff open a program, and a
 * student opens their own placements, which is a different page rather than a
 * dimmer version of the same one.
 *
 * Exported so Spotlight and Campus mosaic rows offer the same pair without
 * re-deriving any of it.
 */
export function useOpenAsIdentity(
  onOpen: (card: ProductHomeCard) => void,
): (card: ProductHomeCard, role: WorkspaceRole) => void {
  const navigate = useNavigate()
  return React.useCallback(
    (card: ProductHomeCard, role: WorkspaceRole) => {
      openAs(role)
      if (role === "student") {
        navigate(STUDENT_HOME_PATH)
        return
      }
      onOpen(card)
    },
    [navigate, onOpen],
  )
}

/**
 * The grid the three card variants lay `OwnedProductTile` out on.
 *
 * Shared for the same reason the tile is: Storefront, Focus, and Launcher each
 * drew this grid by hand, so a column rule added to one silently disagreed with
 * the other two on the same page width.
 *
 * The rule follows `ProductShelves` below the fold, count and all, so "Your App"
 * and "More from Exxat" break to the same number of columns rather than stacking
 * two-up above a three-up shelf. Including the exception: **four** cards stay
 * two-by-two, because three-then-one reads as a card that failed to load.
 *
 * Under three cards the grid itself is capped — one or two tiles must not stretch
 * across the full reading column just because the shelf below uses three tracks.
 * Cap widths sit near one / two of the three-up tile widths at `xl`.
 *
 * Three columns from `xl` rather than the shelf's `lg`. A tile is denser than a
 * marketing card, since it carries a school and program picker and a full-width
 * `Open` under the name, and at `lg` three of them land near 300px, where the
 * scope line truncates. At 1280 they land near 400px, which is what a shelf card
 * gets at the same width.
 */
export function ownedGridClassName(count: number): string {
  return cn(
    "grid list-none gap-4 p-0",
    count === 1 && "max-w-md",
    count === 2 && "max-w-3xl sm:grid-cols-2",
    count >= 3 && "sm:grid-cols-2",
    count >= 3 && count !== 4 && "xl:grid-cols-3",
  )
}

/**
 * Launcher tile — an owned product as a door rather than a row.
 *
 * Shared by Storefront, Focus, and Launcher: a small grid of equal, bordered
 * cards. The product mark carries the brand colour; the card surface stays
 * plain so the bordered scope picker and outline Open read as the controls.
 *
 * Border, shadow, and a full-width outline `Open` button, not a stretched
 * link over a flat fill — a flat fill only reads as clickable once the
 * pointer is already on it, and this card is the one every buyer decision
 * on `/home` ultimately routes through.
 */
export function OwnedProductTile({
  card,
  onOpen,
  showScope = true,
}: {
  card: ProductHomeCard
  onOpen: (card: ProductHomeCard) => void
  /** False when the page carries one workspace scope in the header (Storefront v2). */
  showScope?: boolean
}) {
  const doors = openableDoors(card)
  const openAsIdentity = useOpenAsIdentity(onOpen)
  // Null on every card but Administrator's. Owned here rather than inside the
  // scope line because Open has to land on the app the line names.
  const appScope = useAdminAppScope(card)
  const scopeRow = appScope ? (
    <AdminAppPicker scope={appScope} variant="card" className="mt-auto" />
  ) : showScope ? (
    <ProductScopeLine card={card} variant="card" className="mt-auto" />
  ) : null

  return (
    <li className="min-w-0 list-none">
      <div className="flex h-full flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-xs transition-shadow hover:shadow-md">
        <div className="flex min-w-0 items-center gap-2.5">
          <ProductMarkTile card={card} />
          <h3 className="min-w-0 truncate text-base font-semibold tracking-tight">
            {card.label}
          </h3>
        </div>

        {scopeRow}

        {doors.length > 1 ? (
          <div className={cn(!scopeRow && "mt-auto")}>
            <OpenDoors card={card} doors={doors} onOpen={openAsIdentity} />
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className={cn("w-full", !scopeRow && "mt-auto")}
            onClick={() => onOpen(appScope ? { ...card, href: appScope.href } : card)}
          >
            Open
            <i className="fa-light fa-arrow-right text-sm" aria-hidden="true" />
          </Button>
        )}
      </div>
    </li>
  )
}

export function NoProductsCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-dashed border-border px-6 py-12 text-center",
        className,
      )}
    >
      <p className="text-sm font-medium">No products yet</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        Your workspace has no Exxat products enabled. An administrator can add one from
        Settings.
      </p>
      <Button asChild variant="outline" size="sm" className="mt-4">
        <Link to="/settings/organization">Open organization settings</Link>
      </Button>
    </div>
  )
}
